/**
 * Reduce an appointment update payload to the fields that would actually change.
 *
 * The Appointment tab's Save builds one `updateAppointmentRecord` input holding
 * every field on the form and sends it whole. That makes the save a blind
 * overwrite: a field the form failed to hydrate is written back as an explicit
 * null and the stored value is destroyed, even though the user never touched
 * that control. That is how MA / scheduler / on-site-op assignments were being
 * cleared by saves that only edited interpreter info — the dash-view list query
 * seeds the appointment detail cache with a shallower record (see
 * `useAppointmentsList`), the form hydrates from it before the full fetch lands,
 * and edit mode then freezes those nulls in place.
 *
 * Pruning the payload against the record the form was hydrated from fixes that
 * at the root: a field missing from *both* sides never enters the mutation, so
 * a shallow record can no longer erase anything. This holds as the list query
 * gets slimmer, which is the direction it is heading — a field dropped from the
 * list selection set stops being a wipe hazard rather than becoming one.
 *
 * **The invariant: this never changes what a write does, it only omits writes
 * that would be no-ops.** A field whose payload value differs from the stored
 * one is always sent, unchanged. Comparison is deep and includes keys present
 * only on the stored side, so an object the payload would narrow (dropping a
 * sub-field the record has) still counts as a change and is still sent.
 */

import { removeTypenames } from "~/common/utils";

/**
 * Composite sort keys whose members must travel together.
 *
 * The `updateAppointmentRecord` preAuth resolvers validate the `byDoctor`,
 * `byLocation`, `byCase` and `byGlobalDateTime` indexes with "When updating any
 * part of the composite sort key ... you must provide all fields for the key",
 * and materialize `date#time` from the merged input. Sending a changed `date`
 * without `time` therefore fails the mutation outright, so any surviving member
 * pulls its whole group back in.
 */
export const ATOMIC_FIELD_GROUPS: readonly (readonly string[])[] = [
  ["date", "time"],
];

/**
 * Values that mean "nothing is stored here".
 *
 * A shallow record carries `undefined` where the form holds `null`, and the
 * form renders a missing list as `[]` — all three describe an unset field and
 * must compare equal, or the very wipe this module exists to prevent would be
 * classified as a change. `false` and `0` are real values and are not empty.
 */
const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * A value reduced to its comparable shape: `__typename` stripped, empty leaves
 * collapsed to `null`, keys ordered. An object left with nothing but empty
 * leaves is itself empty — `{ name: null }` and `null` describe the same
 * stored state.
 */
const canonicalize = (value: unknown): unknown => {
  if (isEmptyValue(value)) {
    return null;
  }
  // Order-sensitive by design: reordering an array is a real change to a
  // DynamoDB list. The cost is that a pure reorder of an order-insensitive
  // field reads as changed and gets written — an extra no-op write, never a
  // missed one.
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (typeof value === "object") {
    const entries = Object.entries(
      removeTypenames(value as Record<string, unknown>),
    )
      .map(([key, nested]) => [key, canonicalize(nested)] as const)
      .filter(([, nested]) => nested !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    if (entries.length === 0) {
      return null;
    }
    return Object.fromEntries(entries);
  }
  return value;
};

/** Whether writing `next` over `stored` would change anything. */
export const isUnchanged = (next: unknown, stored: unknown): boolean =>
  JSON.stringify(canonicalize(next)) === JSON.stringify(canonicalize(stored));

/**
 * `input` minus every field already equal to its stored value.
 *
 * `stored` **must be the record the form was hydrated from, not the caller's
 * current one.** These diverge: an editing tab freezes its form but not its
 * `appointment` prop, which keeps refreshing from the subscription, so by save
 * time the live record may already carry values the frozen form does not.
 * Diffing against that reports the form's stale nulls as deliberate clears and
 * re-opens the wipe. Against the hydration source they compare equal and the
 * field is correctly omitted. Passing no record returns the input untouched:
 * with nothing to compare against, the safe move is the old behavior.
 */
export function pruneUnchangedFields<T extends { id: string }>(
  input: T,
  stored: Record<string, unknown> | null | undefined,
): T {
  if (!stored) {
    return input;
  }

  // A generic walk over a typed payload; `Object.entries` needs the index
  // signature that `UpdateAppointmentRecordInput` does not declare.
  const fields = input as unknown as Record<string, unknown>;
  const pruned: Record<string, unknown> = { id: input.id };

  for (const [key, value] of Object.entries(fields)) {
    // `undefined` is dropped by JSON serialization, so keeping such a key
    // would make `hasFieldUpdates` claim an update the wire never carries —
    // an `{id}`-only mutation that bumps `updatedAt` and wakes every
    // subscriber for nothing. A field cleared with `undefined` instead of
    // `null` never reached the server before this either; it is dropped here
    // so the payload contains only what is actually transmitted.
    if (
      key === "id" ||
      value === undefined ||
      isUnchanged(value, stored[key])
    ) {
      continue;
    }
    pruned[key] = value;
  }

  for (const group of ATOMIC_FIELD_GROUPS) {
    if (!group.some((field) => field in pruned)) {
      continue;
    }
    for (const field of group) {
      if (field in fields && fields[field] !== undefined) {
        pruned[field] = fields[field];
      }
    }
  }

  return pruned as unknown as T;
}

/** Whether a pruned payload still carries anything beyond the record's id. */
export const hasFieldUpdates = (input: { id: string }): boolean =>
  Object.keys(input as unknown as Record<string, unknown>).some(
    (key) => key !== "id",
  );
